import { useState } from "react";

import dayjs from "dayjs";

import {
  Box,
  Grid,
  Typography,
  Chip,
  Tooltip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import LockIcon from "@mui/icons-material/Lock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faXmark,
  faRotateRight,
  faPaperPlane,
  faBan
} from "@fortawesome/free-solid-svg-icons";

import { LoadingButton } from "@aclymatepackages/atoms";
import { formatDecimal, letterSBoolean } from "@aclymatepackages/formatters";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";

import GrayPaper from "../../../../atoms/GrayPaper";

import {
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../../../helpers/firebase";
import {
  fetchOurApi,
  requestProductBom
} from "../../../../../helpers/utils/apiCalls";
import {
  getSurveyYearsForBuyer,
  surveyDataStatusMap,
  updateSurveyDataEntry
} from "../../../../../helpers/components/vendors";
import useAccountingData from "../../../../../helpers/hooks/accountingData";
import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";
import usePermissions from "../../../../../helpers/hooks/usePermissions";

const activeSurveyStatuses = new Set([
  "requested",
  "pending",
  "unconfirmed",
  "accepted"
]);
const activeBomStatuses = new Set(["requested", "confirmed"]);

const bomStatusChipProps = {
  requested: { label: "Requested", color: "warning" },
  confirmed: { label: "Shared by Vendor", color: "success" }
};

const StatusChip = ({ status }) => {
  const config = surveyDataStatusMap[status];
  if (!config) return null;
  return (
    <Chip
      size="small"
      label={config.label}
      color={config.chipColor}
      variant="outlined"
    />
  );
};

const noContactEmailTooltip =
  "Add a contact with an email address to request data from this vendor.";

const EmissionsActionCell = ({
  status,
  year,
  tonsCo2ePerDollar,
  isLoading,
  canRequestData,
  canShare,
  onAccept,
  onReject,
  onRevoke,
  onCancel,
  onReRequest
}) => {
  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  const actionMap = {
    pending: [
      {
        key: "accept",
        icon: faCircleCheck,
        tooltip: `Accept (${formatDecimal(tonsCo2ePerDollar)} tCO₂e/$)`,
        color: "primary",
        handler: () => onAccept(year)
      },
      {
        key: "reject",
        icon: faBan,
        tooltip: "Reject shared emissions data",
        color: "error",
        handler: () => onReject(year)
      }
    ],
    unconfirmed: [
      {
        key: "request",
        icon: faPaperPlane,
        tooltip: canRequestData
          ? "Request emissions data"
          : noContactEmailTooltip,
        color: "primary",
        handler: () => onReRequest(year),
        disabled: !canRequestData
      }
    ],
    requested: [
      {
        key: "cancel",
        icon: faXmark,
        tooltip: "Cancel request",
        color: "default",
        handler: () => onCancel(year)
      }
    ],
    accepted: [
      {
        key: "revoke",
        icon: faBan,
        tooltip: "Revoke accepted emissions data",
        color: "error",
        handler: () => onRevoke(year)
      }
    ],
    revoked: [
      {
        key: "rerequest",
        icon: faRotateRight,
        tooltip: canRequestData
          ? "Re-request emissions data"
          : noContactEmailTooltip,
        color: "primary",
        handler: () => onReRequest(year),
        disabled: !canRequestData
      }
    ]
  };

  const actions = actionMap[status];
  if (!actions) return null;

  return (
    <Box display="flex" justifyContent="flex-end">
      {actions.map((action) => (
        <Tooltip key={action.key} title={action.tooltip}>
          <span>
            <IconButton
              size="small"
              color={action.color}
              aria-label={action.tooltip}
              onClick={action.handler}
              disabled={action.disabled || !canShare}
            >
              <FontAwesomeIcon icon={action.icon} />
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
};

const BomActionCell = ({
  bomStatus,
  lifecycleId,
  isLoading,
  onCancel,
  canShare
}) => {
  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (bomStatus !== "requested") return null;

  return (
    <Tooltip title="Cancel request">
      <IconButton
        size="small"
        onClick={() => onCancel(lifecycleId)}
        disabled={!canShare}
      >
        <FontAwesomeIcon icon={faXmark} />
      </IconButton>
    </Tooltip>
  );
};

const SurveyAcceptPreviewModal = ({
  year,
  surveyData,
  transactions,
  currentTonsCo2ePerDollar,
  mostRecentAccountingDate,
  onConfirm,
  onClose
}) => {
  const { palette } = useTheme();
  const { convertCarbonUnits, carbonUnitLabel } = useDisplayMeasurementSystem();

  const surveyEntry =
    (surveyData || []).find((e) => Number(e.year) === year) || {};
  const newTonsCo2ePerDollar = surveyEntry.tonsCo2ePerDollar;

  const yearTransactions = (transactions || []).filter(
    ({ date }) => dayjs(date).year() === year
  );

  const isLocked = ({ date }) =>
    mostRecentAccountingDate
      ? !dayjs(date).isAfter(mostRecentAccountingDate)
      : false;

  const nonLockedTransactions = yearTransactions.filter((t) => !isLocked(t));
  const lockedTransactions = yearTransactions.filter(isLocked);

  const sumTransactionCount = (txns) =>
    txns.reduce(
      (sum, { transactionsCount }) => sum + (transactionsCount || 0),
      0
    );

  const nonLockedCount = sumTransactionCount(nonLockedTransactions);
  const lockedCount = sumTransactionCount(lockedTransactions);

  const oldEmissions = sumTonsCo2e(nonLockedTransactions);
  const spendTotal = currentTonsCo2ePerDollar
    ? oldEmissions / currentTonsCo2ePerDollar
    : 0;
  const newEmissions = spendTotal * (newTonsCo2ePerDollar || 0);
  const emissionsDelta = newEmissions - oldEmissions;

  const isIncrease = emissionsDelta > 0;
  const deltaColor = isIncrease ? palette.error.main : palette.secondary.main;
  const DeltaIcon = isIncrease ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review Emissions Change for {year}</DialogTitle>
      <DialogContent>
        <Box pt={1}>
          <Grid container direction="column" spacing={2}>
            <Grid item container spacing={1}>
              <Grid item>
                <Chip
                  size="small"
                  label={`${nonLockedCount} transaction${letterSBoolean(nonLockedCount)} will be recalculated`}
                  variant="outlined"
                />
              </Grid>
              {lockedCount > 0 && (
                <Grid item>
                  <Tooltip title="These transactions are locked and won't be changed">
                    <Chip
                      size="small"
                      icon={<LockIcon />}
                      label={`${lockedCount} locked`}
                      color="warning"
                      variant="outlined"
                    />
                  </Tooltip>
                </Grid>
              )}
            </Grid>
            <Grid item>
              <GrayPaper>
                <Grid container spacing={1} alignItems="center" wrap="nowrap">
                  <Grid item>
                    <Typography variant="body2">
                      {formatDecimal(convertCarbonUnits(oldEmissions))}{" "}
                      {carbonUnitLabel}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <DeltaIcon style={{ color: deltaColor }} fontSize="small" />
                  </Grid>
                  <Grid item>
                    <Typography variant="body2">
                      {formatDecimal(convertCarbonUnits(newEmissions))}{" "}
                      {carbonUnitLabel}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" style={{ color: deltaColor }}>
                  {isIncrease ? "+" : ""}
                  {formatDecimal(convertCarbonUnits(emissionsDelta))}{" "}
                  {carbonUnitLabel} to your total footprint
                </Typography>
              </GrayPaper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm}>
          Accept Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DataSharingTab = ({
  relationshipId,
  isStripeCustomer,
  isCheckingSubscription,
  vendorSurveys = [],
  surveyData = [],
  linkedProducts = [],
  currentProductId = null,
  hideEmissionsFactors = false,
  transactions = [],
  tonsCo2ePerDollar,
  vendorContacts = [],
  vendorStartDate = null
}) => {
  const { palette } = useTheme();
  const [accountingYears] = useCachedDisplayData("accounting");
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [{ mostRecentAccountingDate }] = useAccountingData();
  const { canShare } = usePermissions();

  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isRequestingEmissions, setIsRequestingEmissions] = useState(false);
  const [isRequestingProducts, setIsRequestingProducts] = useState(false);
  const [loadingYear, setLoadingYear] = useState(null);
  const [loadingLifecycleId, setLoadingLifecycleId] = useState(null);
  const [previewYear, setPreviewYear] = useState(null);

  if (isCheckingSubscription) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const hasContactWithEmail = (vendorContacts || []).some(
    ({ email }) => !!email
  );

  const currentYear = dayjs().year();
  const availableYears = [
    ...new Set([
      ...getSurveyYearsForBuyer(vendorStartDate, accountingYears),
      ...vendorSurveys
        .map(({ year }) => Number(year))
        .filter((year) => year < currentYear)
    ])
  ].sort((a, b) => b - a);

  const getYearStatus = (year) =>
    (surveyData || []).find((e) => Number(e.year) === year)?.status;

  const selectableYears = availableYears.filter(
    (year) => !activeSurveyStatuses.has(getYearStatus(year))
  );
  const hasSelectableYears = selectableYears.length > 0;

  const toggleYear = (year) =>
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );

  const selectableProducts = linkedProducts.filter(
    ({ bomStatus }) => !activeBomStatuses.has(bomStatus)
  );
  const hasSelectableProducts = selectableProducts.length > 0;

  const toggleProduct = (id) =>
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const onRequestEmissions = async () => {
    setIsRequestingEmissions(true);
    try {
      const result = await fetchOurApi({
        path: "/vendors/request-vendor-emissions-data",
        method: "POST",
        data: { relationshipId, years: selectedYears },
        callback: (res) => res
      });
      if (result?.surveyData) {
        updateCollectionDoc("vendor-relationships", relationshipId, {
          surveyData: result.surveyData
        });
      }
      setSelectedYears([]);
    } finally {
      setIsRequestingEmissions(false);
    }
  };

  const getYearEntry = (year) =>
    (surveyData || []).find((e) => Number(e.year) === year) || {};

  const onAcceptYear = async (year) => {
    setLoadingYear(year);
    try {
      const result = await fetchOurApi({
        path: "/vendors/update-survey-transactions",
        method: "POST",
        data: { relationshipId, year },
        callback: (res) => res
      });
      if (result?.success) {
        const updatedSurveyData = updateSurveyDataEntry(surveyData, year, {
          status: "accepted"
        });
        updateCollectionDoc("vendor-relationships", relationshipId, {
          surveyData: updatedSurveyData
        });
      }
    } finally {
      setLoadingYear(null);
    }
  };

  const onRejectSharedYear = (year) => {
    const updatedSurveyData = updateSurveyDataEntry(surveyData, year, {
      status: "revoked"
    });
    updateCollectionDoc("vendor-relationships", relationshipId, {
      surveyData: updatedSurveyData
    });
  };

  const onRevokeAcceptedYear = async (year) => {
    setLoadingYear(year);
    const updatedSurveyData = updateSurveyDataEntry(surveyData, year, {
      status: "revoked"
    });
    updateCollectionDoc("vendor-relationships", relationshipId, {
      surveyData: updatedSurveyData
    });
    try {
      await fetchOurApi({
        path: "/vendors/revoke-vendor-survey-data",
        method: "POST",
        data: { relationshipId, year },
        callback: (res) => res
      });
    } finally {
      setLoadingYear(null);
    }
  };

  const onCancelEmissionsRequest = (year) => {
    const updatedSurveyData = (surveyData || []).filter(
      (e) => Number(e.year) !== year
    );
    updateCollectionDoc("vendor-relationships", relationshipId, {
      surveyData: updatedSurveyData
    });
  };

  const onReRequestEmissions = async (year) => {
    setLoadingYear(year);
    try {
      const result = await fetchOurApi({
        path: "/vendors/request-vendor-emissions-data",
        method: "POST",
        data: { relationshipId, years: [year] },
        callback: (res) => res
      });
      if (result?.surveyData) {
        updateCollectionDoc("vendor-relationships", relationshipId, {
          surveyData: result.surveyData
        });
      }
    } finally {
      setLoadingYear(null);
    }
  };

  const onCancelBomRequest = async (lifecycleId) => {
    setLoadingLifecycleId(lifecycleId);
    await updateCollectionDoc("product-lifecycle-events", lifecycleId, {
      bomStatus: "pending"
    });
    setLoadingLifecycleId(null);
  };

  const onRequestProducts = async () => {
    setIsRequestingProducts(true);
    try {
      const selectedProducts = linkedProducts.filter(({ id }) =>
        selectedProductIds.includes(id)
      );
      await Promise.all(
        selectedProducts.map(async ({ id: productId, lifecycleId }) => {
          const result = await requestProductBom({ productId, relationshipId });
          if (result?.success) {
            updateCollectionDoc("product-lifecycle-events", lifecycleId, {
              bomStatus: "requested"
            });
          }
        })
      );
      setSelectedProductIds([]);
    } finally {
      setIsRequestingProducts(false);
    }
  };

  return (
    <Box p={2}>
      {previewYear !== null && (
        <SurveyAcceptPreviewModal
          year={previewYear}
          surveyData={surveyData}
          transactions={transactions}
          currentTonsCo2ePerDollar={tonsCo2ePerDollar}
          mostRecentAccountingDate={mostRecentAccountingDate}
          onConfirm={() => {
            onAcceptYear(previewYear);
            setPreviewYear(null);
          }}
          onClose={() => setPreviewYear(null)}
        />
      )}
      <Grid container direction="column" spacing={2}>
        {!!availableYears.length && !hideEmissionsFactors && (
          <Grid item>
            <GrayPaper>
              <Box p={2}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="subtitle1">
                      Annual Company Footprint
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {hasSelectableYears && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  selectedYears.length ===
                                  selectableYears.length
                                }
                                indeterminate={
                                  selectedYears.length > 0 &&
                                  selectedYears.length < selectableYears.length
                                }
                                onChange={(e) =>
                                  e.target.checked
                                    ? setSelectedYears(selectableYears)
                                    : setSelectedYears([])
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell>Year</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell padding="checkbox" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableYears.map((year) => {
                          const status = getYearStatus(year);
                          const isActive = activeSurveyStatuses.has(status);
                          const isSelected = selectedYears.includes(year);
                          const entry = getYearEntry(year);
                          return (
                            <TableRow key={year}>
                              {hasSelectableYears && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isActive || isSelected}
                                    disabled={isActive}
                                    onChange={() => toggleYear(year)}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2">{year}</Typography>
                              </TableCell>
                              <TableCell>
                                <StatusChip status={status} />
                              </TableCell>
                              <TableCell padding="checkbox">
                                <EmissionsActionCell
                                  status={status}
                                  year={year}
                                  tonsCo2ePerDollar={entry.tonsCo2ePerDollar}
                                  isLoading={loadingYear === year}
                                  canRequestData={hasContactWithEmail}
                                  canShare={canShare}
                                  onAccept={setPreviewYear}
                                  onReject={onRejectSharedYear}
                                  onRevoke={onRevokeAcceptedYear}
                                  onCancel={onCancelEmissionsRequest}
                                  onReRequest={onReRequestEmissions}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Grid>
                  {hasSelectableYears && (
                    <Grid item>
                      <Tooltip
                        title={
                          !hasContactWithEmail
                            ? noContactEmailTooltip
                            : selectedYears.length === 0
                              ? "Select one or more years to request data."
                              : ""
                        }
                      >
                        <span>
                          <LoadingButton
                            isLoading={isRequestingEmissions}
                            onClick={onRequestEmissions}
                            variant="contained"
                            color="primary"
                            label="Request Selected"
                            disabled={
                              !hasContactWithEmail ||
                              selectedYears.length === 0 ||
                              !canShare
                            }
                          />
                        </span>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </GrayPaper>
          </Grid>
        )}

        {!!linkedProducts.length && (
          <Grid item>
            <GrayPaper>
              <Box p={2}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="subtitle1">Product BOMs</Typography>
                  </Grid>
                  <Grid item>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {hasSelectableProducts && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  selectedProductIds.length ===
                                  selectableProducts.length
                                }
                                indeterminate={
                                  selectedProductIds.length > 0 &&
                                  selectedProductIds.length <
                                    selectableProducts.length
                                }
                                onChange={(e) =>
                                  e.target.checked
                                    ? setSelectedProductIds(
                                        selectableProducts.map(({ id }) => id)
                                      )
                                    : setSelectedProductIds([])
                                }
                              />
                            </TableCell>
                          )}
                          <TableCell>Product</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell padding="checkbox" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {linkedProducts.map((product) => {
                          const { id, name, sku, bomStatus, lifecycleId } =
                            product;
                          const isActive = activeBomStatuses.has(bomStatus);
                          const isSelected = selectedProductIds.includes(id);
                          const chipProps = bomStatusChipProps[bomStatus];
                          return (
                            <TableRow
                              key={id}
                              sx={
                                id === currentProductId
                                  ? {
                                      backgroundColor:
                                        palette.backgroundGray.light
                                    }
                                  : undefined
                              }
                            >
                              {hasSelectableProducts && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={isActive || isSelected}
                                    disabled={isActive}
                                    onChange={() => toggleProduct(id)}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2">
                                  {name || "Unnamed Product"}
                                </Typography>
                                {sku && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    SKU: {sku}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {isActive && chipProps && (
                                  <Chip
                                    size="small"
                                    label={chipProps.label}
                                    color={chipProps.color}
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell padding="checkbox">
                                <BomActionCell
                                  bomStatus={bomStatus}
                                  lifecycleId={lifecycleId}
                                  isLoading={loadingLifecycleId === lifecycleId}
                                  onCancel={onCancelBomRequest}
                                  canShare={canShare}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Grid>
                  {hasSelectableProducts && (
                    <Grid item>
                      <Tooltip
                        title={
                          !hasContactWithEmail
                            ? noContactEmailTooltip
                            : selectedProductIds.length === 0
                              ? "Select one or more products to request data."
                              : ""
                        }
                      >
                        <span>
                          <LoadingButton
                            isLoading={isRequestingProducts}
                            onClick={onRequestProducts}
                            variant="contained"
                            color="primary"
                            label="Request Selected"
                            disabled={
                              !hasContactWithEmail ||
                              selectedProductIds.length === 0 ||
                              !canShare
                            }
                          />
                        </span>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </GrayPaper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
export default DataSharingTab;
